import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import { useDebounce } from "use-debounce";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface SearchProps {
  selectedResult?: google.maps.places.AutocompletePrediction;
  onSelectResult: (product: google.maps.places.AutocompletePrediction) => void;
}

export function Search({ selectedResult, onSelectResult }: SearchProps) {
  const [searchQuery, setSearchQuery] = React.useState("");

  const handleSelectResult = (
    product: google.maps.places.AutocompletePrediction,
  ) => {
    onSelectResult(product);

    // OPTIONAL: reset the search query upon selection
    // setSearchQuery('');
  };

  return (
    <Command
      shouldFilter={false}
      className="h-auto rounded-lg border border-b-0 shadow-md"
    >
      <CommandInput
        value={searchQuery}
        onValueChange={setSearchQuery}
        placeholder="Search for a place"
      />

      <SearchResults
        query={searchQuery}
        selectedResult={selectedResult}
        onSelectResult={handleSelectResult}
      />
    </Command>
  );
}

interface SearchResultsProps {
  query: string;
  selectedResult: SearchProps["selectedResult"];
  onSelectResult: SearchProps["onSelectResult"];
}

function SearchResults({
  query,
  selectedResult,
  onSelectResult,
}: SearchResultsProps) {
  const [debouncedSearchQuery] = useDebounce(query, 500);
  const [isLoadingOrig, setIsLoadingOrig] = React.useState(false);

  const enabled = !!debouncedSearchQuery;

  const [places, setPlaces] = React.useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  React.useEffect(() => {
    (async () => {
      if (!enabled) return;
      setIsLoadingOrig(true);

      const { AutocompleteService } = (await google.maps.importLibrary(
        "places",
      )) as google.maps.PlacesLibrary;

      const n = new AutocompleteService();
      const request: google.maps.places.AutocompleteRequest = {
        input: debouncedSearchQuery,
      };
      const { predictions } = await n.getPlacePredictions(request);
      console.log(predictions);
      setPlaces(predictions);
      setIsLoadingOrig(false);
    })();
  }, [debouncedSearchQuery, enabled]);

  // To get around this https://github.com/TanStack/query/issues/3584
  const isLoading = isLoadingOrig;

  return (
    <CommandList>
      {/* TODO: these should have proper loading aria */}
      {isLoading && <div className="p-4 text-sm">Searching...</div>}
      {places.length === 0 && !isLoading && (
        <div className="p-4 text-sm">No places found</div>
      )}

      {!!places.length &&
        places.map((place) => {
          const { place_id: id, description } = place;
          return (
            <CommandItem
              key={id}
              onSelect={() => onSelectResult(place)}
              value={id}
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  selectedResult?.place_id === id ? "opacity-100" : "opacity-0",
                )}
              />
              {description}
            </CommandItem>
          );
        })}
    </CommandList>
  );
}

const POPOVER_WIDTH = "w-[250px]";

export function GooglePlacesAutocomplete({
  onSelect,
  placeId,
}: {
  onSelect?: (place: google.maps.places.AutocompletePrediction) => void;
  placeId?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<
    google.maps.places.AutocompletePrediction | undefined
  >();

  const handleSetActive = React.useCallback(
    (product: google.maps.places.AutocompletePrediction) => {
      setSelected(product);

      onSelect?.(product);
      setOpen(false);
    },
    [onSelect],
  );

  React.useEffect(() => {
    if (selected && placeId !== selected.place_id) setSelected(undefined);
  }, [selected, placeId]);

  const displayName = selected
    ? selected.structured_formatting.main_text
    : "Select a place";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn("justify-between", POPOVER_WIDTH)}
        >
          {displayName}

          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent side="bottom" className={cn("p-0", POPOVER_WIDTH)}>
        <Search selectedResult={selected} onSelectResult={handleSetActive} />
      </PopoverContent>
    </Popover>
  );
}
